import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button, Form, Header, Label, Message, Segment, Table,
} from "semantic-ui-react";
import apis from "../../../apis";
import {
  formatDuration, formatRate, isValidPeriod, toDateTimeInputValue,
} from "../formatters";

const DAY_MS = 24 * 60 * 60 * 1000;
const LABELS = {
  new: { color: "orange", text: "New issue pattern" },
  worse: { color: "red", text: "Worse" },
  recurring: { color: "yellow", text: "Recurring" },
  recovered: { color: "green", text: "Recovered" },
  insufficient_data: { color: "grey", text: "Not enough data" },
};

function presetRange(preset) {
  const to = new Date();
  if (preset === "today") {
    return { from: new Date(to.getFullYear(), to.getMonth(), to.getDate()), to };
  }
  return { from: new Date(to.getTime() - (preset === "30d" ? 30 : 7) * DAY_MS), to };
}

function formatChange(value, suffix = "") {
  if (value === null || value === undefined) return "-";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}${suffix}`;
}

function formatDurationChange(value) {
  if (value === null || value === undefined) return "-";
  const sign = value > 0 ? "+" : "";
  return Math.abs(value) < 1000
    ? `${sign}${Math.round(value)} ms`
    : `${sign}${(value / 1000).toFixed(1)} s`;
}

const ComparisonView = ({ automationId, workflowVersion }) => {
  const initialRange = presetRange("7d");
  const [preset, setPreset] = useState("7d");
  const [fromInput, setFromInput] = useState(toDateTimeInputValue(initialRange.from));
  const [toInput, setToInput] = useState(toDateTimeInputValue(initialRange.to));
  const [range, setRange] = useState(initialRange);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestGeneration = useRef(0);
  const requestKey = `${automationId}|${workflowVersion}|${range.from.toISOString()}|${range.to.toISOString()}`;
  const comparison = comparisonResult?.key === requestKey ? comparisonResult.data : null;

  const loadComparison = useCallback(async () => {
    if (!automationId || !workflowVersion) return;
    const generation = ++requestGeneration.current;
    setLoading(true);
    setComparisonResult(null);
    try {
      const { data } = await apis.automationMonitoring.getComparison({
        automationId,
        workflowVersion,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      });
      if (generation === requestGeneration.current) {
        setComparisonResult({ data, key: requestKey });
        setError("");
      }
    } catch (requestError) {
      if (generation === requestGeneration.current) {
        setError(requestError.response?.data?.message || "Could not compare these periods.");
      }
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [automationId, range, requestKey, workflowVersion]);

  useEffect(() => {
    loadComparison();
    return () => {
      requestGeneration.current += 1;
    };
  }, [loadComparison]);

  const invalidateComparison = () => {
    requestGeneration.current += 1;
    setComparisonResult(null);
    setLoading(false);
  };

  const choosePreset = (value) => {
    invalidateComparison();
    setPreset(value);
    if (value === "custom") return;
    const nextRange = presetRange(value);
    setFromInput(toDateTimeInputValue(nextRange.from));
    setToInput(toDateTimeInputValue(nextRange.to));
    setRange(nextRange);
  };

  const applyCustomRange = () => {
    invalidateComparison();
    const from = new Date(fromInput);
    const to = new Date(toInput);
    if (!isValidPeriod(from, to)) {
      setError("Choose a valid period of 31 days or less.");
      return;
    }
    setRange({ from, to });
  };

  const label = comparison?.label ? LABELS[comparison.label] : null;
  const comparisonLabel = label
    ? <Label color={label.color}>{label.text}</Label>
    : <Label basic>Neutral</Label>;
  const rows = comparison ? [
    ["Runs", comparison.current.total, comparison.previous.total, comparison.current.total - comparison.previous.total],
    ["Completed", comparison.current.outcomes.succeeded, comparison.previous.outcomes.succeeded, comparison.change.completedCount],
    ["Failed", comparison.current.outcomes.failed, comparison.previous.outcomes.failed, comparison.change.failedCount],
    ["Stuck", comparison.current.outcomes.stuck, comparison.previous.outcomes.stuck, comparison.change.stuckCount],
    ["Completion rate", formatRate(comparison.current.completionRate), formatRate(comparison.previous.completionRate), formatChange(comparison.change.completionRatePercentagePoints, " pp")],
    ["Failure rate", formatRate(comparison.current.failureRate), formatRate(comparison.previous.failureRate), formatChange(comparison.change.failureRatePercentagePoints, " pp")],
    ["Stuck rate", formatRate(comparison.current.stuckRate), formatRate(comparison.previous.stuckRate), formatChange(comparison.change.stuckRatePercentagePoints, " pp")],
    ["Affected rate", formatRate(comparison.current.affectedRate), formatRate(comparison.previous.affectedRate), formatChange(comparison.change.affectedRatePercentagePoints, " pp")],
    ["Median duration", formatDuration(comparison.current.duration.medianDurationMs), formatDuration(comparison.previous.duration.medianDurationMs), formatDurationChange(comparison.change.medianDurationMs)],
    ["P95 duration", formatDuration(comparison.current.duration.p95DurationMs), formatDuration(comparison.previous.duration.p95DurationMs), formatDurationChange(comparison.change.p95DurationMs)],
  ] : [];

  return (
    <Segment className="automation-monitoring__analysis-section">
      <div className="automation-monitoring__section-heading">
        <Header as="h2">Period comparison</Header>
        {comparison ? comparisonLabel : null}
      </div>
      <Button.Group size="small" aria-label="Comparison period">
        {["today", "7d", "30d", "custom"].map((value) => (
          <Button key={value} active={preset === value} onClick={() => choosePreset(value)}>
            {value === "today" ? "Today" : value === "custom" ? "Custom" : value}
          </Button>
        ))}
      </Button.Group>
      {preset === "custom" ? (
        <Form className="automation-monitoring__inline-form" onSubmit={applyCustomRange}>
          <Form.Input
            label="From"
            type="datetime-local"
            value={fromInput}
            onChange={(event) => {
              invalidateComparison();
              setFromInput(event.target.value);
            }}
          />
          <Form.Input
            label="To"
            type="datetime-local"
            value={toInput}
            onChange={(event) => {
              invalidateComparison();
              setToInput(event.target.value);
            }}
          />
          <Button type="submit">Apply</Button>
        </Form>
      ) : null}
      {error ? <Message negative content={error} /> : null}
      {loading && !comparison ? <Message content="Loading comparison..." /> : null}
      {comparison ? (
        <div className="automation-monitoring__table-scroll">
          <Table celled compact>
            <Table.Header><Table.Row><Table.HeaderCell>Metric</Table.HeaderCell><Table.HeaderCell>Current</Table.HeaderCell><Table.HeaderCell>Previous</Table.HeaderCell><Table.HeaderCell>Change</Table.HeaderCell></Table.Row></Table.Header>
            <Table.Body>{rows.map(([name, current, previous, change]) => (
              <Table.Row key={name}><Table.Cell>{name}</Table.Cell><Table.Cell>{current}</Table.Cell><Table.Cell>{previous}</Table.Cell><Table.Cell>{change}</Table.Cell></Table.Row>
            ))}</Table.Body>
          </Table>
        </div>
      ) : null}
    </Segment>
  );
};

ComparisonView.propTypes = {
  automationId: PropTypes.string.isRequired,
  workflowVersion: PropTypes.string,
};

ComparisonView.defaultProps = {
  workflowVersion: "",
};

export default ComparisonView;
